import toast from 'react-hot-toast';
import * as React from 'react';

type NotificationType = 'success' | 'error' | 'loading' | 'info' | 'warning';

interface NotificationOptions {
  loading?: string;
  success?: string | React.ReactNode;
  error: string | React.ReactNode | ((error: any) => string | React.ReactNode);
  method: () => Promise<any>;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  id?: string;
}

interface ToastConfig {
  duration?: number;
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  style?: React.CSSProperties;
  className?: string;
  icon?: string | React.ReactNode;
  id?: string;
}

const defaultConfig: ToastConfig = {
  duration: 5000,
  position: 'top-right',
};

// Track active notifications to prevent duplicates
const activeNotifications = new Map<string, Promise<any>>();

/**
 * Shows a toast notification with loading, success, and error states
 */
export const showNotification = async ({
  loading,
  success,
  error,
  method,
  onSuccess,
  onError,
  id,
}: NotificationOptions) => {
  const toastId = id || Date.now().toString();
  
  // If we have a specific ID and it's already active, return the existing promise
  if (id && activeNotifications.has(id)) {
    return activeNotifications.get(id);
  }
  
  const notificationPromise = (async () => {
    try {
      // Show loading state only if loading message is provided
      if (loading) {
        showLoading(loading, { id: toastId });
      }
      
      // Execute the method
      const result = await method();
      
      // Show success and call onSuccess callback if provided
      // toast.success() automatically replaces loading toasts with the same ID
      if (success) {
        showSuccess(success, { id: toastId });
      }
      if (onSuccess) onSuccess(result);
      
      return result;
    } catch (err: any) {
      // Show error and call onError callback if provided
      // toast.error() automatically replaces loading toasts with the same ID
      const errorMessage = (err?.response?.data?.message as string) || 
        (typeof error === 'function' ? error(err) : error);
      
      showError(errorMessage as string, { id: toastId });
      if (onError) onError(err);
      throw err;
    } finally {
      // Clean up the active notification
      if (id) {
        activeNotifications.delete(id);
      }
    }
  })();
  
  // Store the promise if we have an ID
  if (id) {
    activeNotifications.set(id, notificationPromise);
  }
  
  return notificationPromise;
};

/**
 * Shows a success notification
 * If an ID is provided and a loading toast exists with that ID, it will be replaced
 */
export const showSuccess = (message: string | React.ReactNode, config?: ToastConfig) => {
  const finalConfig = { ...defaultConfig, ...config };
  const content = typeof message === 'string' 
    ? message 
    : React.isValidElement(message) 
      ? message 
      : String(message);
  
  // If an ID is provided, use toast.success() which automatically replaces loading toasts
  if (finalConfig.id) {
    return toast.success(content, {
      duration: finalConfig.duration,
      position: finalConfig.position,
      id: finalConfig.id,
      style: {
        ...finalConfig.style,
        background: '#10B981',
        color: '#FFFFFF',
      }
    });
  }
      
  return toast.custom((t) => (
    React.createElement('div', {
      className: `${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-[#10B981] shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`,
      style: {
        padding: '16px 24px',
        fontSize: '14px',
        color: '#FFFFFF',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        ...finalConfig.style
      }
    }, [
      React.createElement('div', {
        key: 'content',
        className: 'flex-1 flex items-center'
      }, [
        React.createElement('div', {
          key: 'icon',
          className: 'mr-3'
        }, finalConfig.icon || 'OK'),
        React.createElement('div', { className: 'flex-1' }, content)
      ]),
      React.createElement('button', {
        key: 'close',
        onClick: () => toast.dismiss(t.id),
        className: 'ml-4 text-white hover:text-gray-200 focus:outline-none transition-colors duration-200',
        'aria-label': 'Close notification'
      }, '×')
    ])
  ), { 
    duration: finalConfig.duration,
    position: finalConfig.position
  });
};

/**
 * Shows an error notification
 * If an ID is provided and a loading toast exists with that ID, it will be replaced
 */
export const showError = (message: string | React.ReactNode, config?: ToastConfig) => {
  const finalConfig = { ...defaultConfig, ...config };
  const content = typeof message === 'string' 
    ? message 
    : React.isValidElement(message) 
      ? message 
      : String(message);
  
  // If an ID is provided, use toast.error() which automatically replaces loading toasts
  if (finalConfig.id) {
    return toast.error(content, {
      duration: finalConfig.duration,
      position: finalConfig.position,
      id: finalConfig.id,
      style: {
        ...finalConfig.style,
        background: '#DC2626',
        color: '#FFFFFF',
      }
    });
  }
  
  return toast.custom((t) => (
    React.createElement('div', {
      className: `${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-[#DC2626] shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`,
      style: {
        padding: '16px 24px',
        fontSize: '14px',
        color: '#FFFFFF',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        ...finalConfig.style
      }
    }, [
      React.createElement('div', {
        key: 'content',
        className: 'flex-1 flex items-center'
      }, [
        React.createElement('div', {
          key: 'icon',
          className: 'mr-3'
        }, finalConfig.icon || 'X'),
        React.createElement('div', { className: 'flex-1' }, content)
      ]),
      React.createElement('button', {
        key: 'close',
        onClick: () => toast.dismiss(t.id),
        className: 'ml-4 text-white hover:text-gray-200 focus:outline-none transition-colors duration-200',
        'aria-label': 'Close notification'
      }, '×')
    ])
  ), { 
    duration: finalConfig.duration,
    position: finalConfig.position
  });
};

/**
 * Shows a loading notification
 */
export const showLoading = (message: string, config?: ToastConfig) => {
  const finalConfig = { ...defaultConfig, ...config };
  // Convert ReactNode to string for loading toast since react-hot-toast loading only accepts string
  const loadingMessage = typeof message === 'string' ? message : 'Loading...';
  return toast.loading(loadingMessage, {
    duration: finalConfig.duration,
    position: finalConfig.position,
    style: finalConfig.style,
    className: finalConfig.className,
    id: finalConfig.id
  });
};

/**
 * Shows an info notification
 */
export const showInfo = (message: string | React.ReactNode, config?: ToastConfig) => {
  const finalConfig = { ...defaultConfig, ...config };
  const content = typeof message === 'string' 
    ? message 
    : React.isValidElement(message) 
      ? message 
      : String(message);
      
  return toast.custom((t) => (
    React.createElement('div', {
      className: `${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-[#3B82F6] shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`,
      style: {
        padding: '16px 24px',
        fontSize: '14px',
        color: '#FFFFFF',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        ...finalConfig.style
      }
    }, [
      React.createElement('div', {
        key: 'content',
        className: 'flex-1 flex items-center'
      }, [
        React.createElement('div', {
          key: 'icon',
          className: 'mr-3'
        }, finalConfig.icon || 'ℹ️'),
        React.createElement('div', { className: 'flex-1' }, content)
      ]),
      React.createElement('button', {
        key: 'close',
        onClick: () => toast.dismiss(t.id),
        className: 'ml-4 text-white hover:text-gray-200 focus:outline-none transition-colors duration-200',
        'aria-label': 'Close notification'
      }, '×')
    ])
  ), { 
    duration: finalConfig.duration,
    position: finalConfig.position
  });
};

/**
 * Shows a warning notification
 */
export const showWarning = (message: string | React.ReactNode, config?: ToastConfig) => {
  const finalConfig = { ...defaultConfig, ...config };
  const content = typeof message === 'string' 
    ? message 
    : React.isValidElement(message) 
      ? message 
      : String(message);
      
  return toast.custom((t) => (
    React.createElement('div', {
      className: `${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-[#F59E0B] shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`,
      style: {
        padding: '16px 24px',
        fontSize: '14px',
        color: '#FFFFFF',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        ...finalConfig.style
      }
    }, [
      React.createElement('div', {
        key: 'content',
        className: 'flex-1 flex items-center'
      }, [
        React.createElement('div', {
          key: 'icon',
          className: 'mr-3'
        }, finalConfig.icon || '!'),
        React.createElement('div', { className: 'flex-1' }, content)
      ]),
      React.createElement('button', {
        key: 'close',
        onClick: () => toast.dismiss(t.id),
        className: 'ml-4 text-white hover:text-gray-200 focus:outline-none transition-colors duration-200',
        'aria-label': 'Close notification'
      }, '×')
    ])
  ), { 
    duration: finalConfig.duration,
    position: finalConfig.position
  });
};

/**
 * Updates an existing notification
 */
export const updateNotification = (
  id: string, 
  type: NotificationType, 
  message: string | React.ReactNode,
  config?: ToastConfig
) => {
  const finalConfig = { ...defaultConfig, ...config };
  
  // Convert ReactNode to string for toast methods that don't support ReactNode
  const messageContent = typeof message === 'string' 
    ? message 
    : React.isValidElement(message) 
      ? message 
      : String(message);

  switch (type) {
    case 'success':
      return showSuccess(messageContent, { ...finalConfig, id });
    case 'error':
      return showError(messageContent, { ...finalConfig, id });
    case 'warning':
      return showWarning(messageContent, { ...finalConfig, id });
    case 'info':
      return showInfo(messageContent, { ...finalConfig, id });
    case 'loading':
      // For loading, we can only pass string to react-hot-toast's loading
      return showLoading(
        typeof messageContent === 'string' ? messageContent : 'Loading...', 
        { ...finalConfig, id }
      );
    default:
      return null;
  }
};

/**
 * Dismisses a specific notification
 */
export const dismissNotification = (id: string) => {
  toast.dismiss(id);
};

/**
 * Dismisses all notifications
 */
export const dismissAllNotifications = () => {
  toast.dismiss();
};

export default {
  showNotification,
  showSuccess,
  showError,
  showLoading,
  showInfo,
  showWarning,
  updateNotification,
  dismissNotification,
  dismissAllNotifications,
};
